<template>
  <div
    class="relative mb-2 mt-2 flex flex-col items-end justify-center gap-2 border-[0.5px] border-gray-200 bg-white p-2"
    :data-testid="`guest-type-selector-${props.experienceEventFee.guestType.typeName}`"
  >
    <div class="flex w-full items-start justify-start gap-2">
      <div class="flex flex-1 flex-col items-start justify-start gap-1">
        <div class="w-full text-base font-normal leading-normal text-gray-700">{{ props.experienceEventFee.guestType.name }}</div>
        <div class="w-full whitespace-pre-wrap text-sm font-normal leading-tight text-gray-500">{{ props.experienceEventFee.guestType.overview }}</div>
        <div v-if="props.experienceEventFee.guestType.description && props.experienceEventFee.guestType.description.trim()" class="flex items-center justify-start gap-2">
          <button class="cursor-pointer border-none bg-transparent p-0 text-base font-normal leading-normal text-gold4 underline" type="button" @click="isDetailDrawerOpen = true">Detail</button>
        </div>
      </div>
    </div>
    <div class="flex w-full items-end justify-end bg-white">
      <div class="flex w-36 items-center justify-between overflow-hidden rounded-[1px] border-[0.5px] border-gray-200">
        <button
          class="flex h-10 w-10 flex-col items-center justify-center gap-0.5 rounded-[1px]"
          :class="{
            'cursor-not-allowed bg-gray10': currentCount === 0 || props.disabled,
            'bg-gold4': !(currentCount === 0 || props.disabled),
          }"
          :disabled="currentCount === 0 || props.disabled"
          @click="handleDecrement"
        >
          <span
            class="text-center text-base font-normal leading-normal"
            :class="{
              'text-gray-300': currentCount === 0 || props.disabled,
              'text-white': !(currentCount === 0 || props.disabled),
            }"
            >－</span
          >
        </button>
        <div class="text-center text-base font-normal leading-normal text-gray-700">{{ currentCount }}</div>
        <button
          class="flex h-10 w-10 flex-col items-center justify-center gap-0.5 rounded-[1px]"
          data-testid="increment-button"
          :class="{
            'cursor-not-allowed bg-gray10': isMaxCountReached || props.disabled,
            'bg-gold4': !(isMaxCountReached || props.disabled),
          }"
          :disabled="isMaxCountReached || props.disabled"
          @click="handleIncrement"
        >
          <span
            class="text-center text-base font-normal leading-normal"
            :class="{
              'text-gray-300': isMaxCountReached || props.disabled,
              'text-white': !(isMaxCountReached || props.disabled),
            }"
            >＋</span
          >
        </button>
      </div>
    </div>

    <p v-if="props.error" class="mt-2 text-red-500">{{ props.error }}</p>
  </div>

  <!-- ゲストタイプ詳細ドロワー -->
  <GuestTypeDetailDrawer :guest-type="props.experienceEventFee.guestType" :is-open="isDetailDrawerOpen" :on-close="() => (isDetailDrawerOpen = false)" />
</template>

<script lang="ts" setup>
import type { WabunkaExperienceEventFeeViewModel } from '@/wabunka-src/features/Experience/models/experienceEventFee';
import GuestTypeDetailDrawer from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/GuestTypeDetailDrawer/GuestTypeDetailDrawer.vue';

interface Props {
  experienceEventFee: WabunkaExperienceEventFeeViewModel;
  count?: number;
  maxCount: number;
  disabled?: boolean;
  error?: string;
  onIncrement: () => void;
  onDecrement: () => void;
}

const props = defineProps<Props>();
const isDetailDrawerOpen = ref(false);

// count のデフォルト値を設定
const currentCount = computed(() => props.count ?? 0);

// 最大数に達したかどうかを計算
const isMaxCountReached = computed(() => {
  // isIncludedInHeadcountLimitがfalseの場合は、100を上限とする
  if (!props.experienceEventFee.guestType.isIncludedInHeadcountLimit) {
    return currentCount.value >= 100;
  }
  return currentCount.value >= props.maxCount;
});

// クリックハンドラー関数
const handleDecrement = () => {
  if (currentCount.value > 0 && !props.disabled) {
    props.onDecrement();
  }
};

const handleIncrement = () => {
  if (!isMaxCountReached.value && !props.disabled) {
    props.onIncrement();
  }
};
</script>
